import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { motion, useDragControls } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { TabType } from './TabSelector';
import TabSelector from './TabSelector';
import AIAssistant from './AIAssistant';
import { useHotkeys } from '../../hooks/useHotkeys';
import useStore from '../../store/useStore';
import { shallow } from 'zustand/shallow'; // Import shallow
import { useIntegrations } from '../../hooks/useIntegrations';
import { useCanvasContext } from '../../contexts/CanvasContext';
import { NodeType } from "../../types";
import { staticItems } from "./constants";
import { getNodeComponentType } from "./utils";
import { SearchBar } from "./SearchBar";
import { DragPreview } from "./DragPreview";
import { MenuSection } from "./MenuSection";
import { useNodeCreator } from "./NodeCreator";
import { LeftPanelProps, MenuItem, StaticItemGroup } from "./types"; // Added StaticItemGroup
import { Integration } from "../../types"; // Added Integration

function LeftPanel({ onAdd, currentProject, isPreviewMode = false }: LeftPanelProps) {
  const [width, setWidth] = useState(360);
  const setIsEmbedded = useState(false)[1];
  const {
    isLeftPanelManuallyCollapsed,
    togglePanels,
  } = useStore(
    (state) => ({
      isLeftPanelManuallyCollapsed: state.isLeftPanelManuallyCollapsed,
      togglePanels: state.togglePanels,
    }),
    shallow,
  );
  
  // Add state for active tab
  const [activeTab, setActiveTab] = useState<TabType>(() => {
    // Load from localStorage or default to 'library'
    const savedTab = localStorage.getItem('leftPanelActiveTab');
    return (savedTab as TabType) || 'library';
  });
  
  // Save active tab to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('leftPanelActiveTab', activeTab);
  }, [activeTab]);

  // Check if we're in embedded mode by looking at the URL
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    setIsEmbedded(searchParams.get('embed') === 'true');
  }, []);
  
  const getPanelTopClass = () => {
    return isPreviewMode ? 'top-0' : 'top-14';
  };
  
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);
  const menuContainerRef = useRef<HTMLDivElement>(null);
  const dragControls = useDragControls();
  const [isDragging, setIsDragging] = useState(false);
  const [draggedItem, setDraggedItem] = useState<MenuItem | null>(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const setKeyboardSelectedNode = useState<MenuItem | null>(null)[1];
  const [favorites, setFavorites] = useState<string[]>(() => {
    // Load favorites from localStorage on initial render
    const savedFavorites = localStorage.getItem('leftPanelFavorites');
    return savedFavorites ? JSON.parse(savedFavorites) : [];
  });

  // Load integrations
  const { integrations, loadIntegrations } = useIntegrations();
  const { reactFlowWrapper, fitView } = useCanvasContext();

  useEffect(() => {
    loadIntegrations().catch(console.error);
  }, [loadIntegrations]);

  // Create node creator hook
  const { handleAdd, createNodeData } = useNodeCreator({
    currentProject,
    onAdd,
    reactFlowWrapper,
    fitView
  });

  // --- Favorites Logic ---
  const handleToggleFavorite = useCallback((itemId: string) => {
    setFavorites(prevFavorites => {
      const newFavorites = prevFavorites.includes(itemId)
        ? prevFavorites.filter(id => id !== itemId)
        : [...prevFavorites, itemId];
      // Save to localStorage
      localStorage.setItem('leftPanelFavorites', JSON.stringify(newFavorites));
      return newFavorites;
    });
  }, []);

  const handleClearFavorites = useCallback(() => {
    setFavorites([]);
    localStorage.setItem('leftPanelFavorites', JSON.stringify([]));
  }, []);

  // --- Item Filtering Logic ---
  // Create a flat list of all possible items (static + integrations)
  const allPossibleItems = useMemo<(StaticItemGroup | Integration)[]>(() => [
    ...staticItems.sources as StaticItemGroup[],
    ...staticItems.modules as StaticItemGroup[],
    ...staticItems.layout as StaticItemGroup[],
    ...staticItems.pipeline as StaticItemGroup[],
    ...integrations
      .filter((integration) => integration.enabled && integration.destination === true)
  ], [integrations]);

  // Filter items based on search term and separate favorites
  const { favoriteItems, regularItems } = useMemo(() => {
    const favItems: (StaticItemGroup | Integration)[] = [];
    const regItems: {
      sources: StaticItemGroup[];
      modules: StaticItemGroup[];
      layout: StaticItemGroup[];
      pipeline: StaticItemGroup[];
      destinations: Integration[];
    } = { sources: [], modules: [], layout: [], pipeline: [], destinations: [] };

    allPossibleItems.forEach(item => {
      const isIntegration = 'logo_url' in item; // Type guard
      // Apply type guard when accessing 'type'
      const itemType = isIntegration ? 'destination' : (item as StaticItemGroup).type;
      const itemId = isIntegration
        ? `destination-${item.id}`
        : `${item.id}-${itemType}`; // Use safe itemType
      const nameMatch = item.name.toLowerCase().includes(searchTerm.toLowerCase());
      const keywordMatch = isIntegration && (item as Integration).keywords?.some((keyword: string) => // Cast for keywords
        keyword.toLowerCase().includes(searchTerm.toLowerCase())
      );

      if (nameMatch || keywordMatch) {
        // Add to favorites if it's a favorite
        if (favorites.includes(itemId)) {
          favItems.push(item);
        }

        // Always add to the regular sections regardless of favorite status
        if (isIntegration) {
          regItems.destinations.push(item as Integration);
        } else {
          // It's a StaticItemGroup
          const staticItem = item as StaticItemGroup;
          if (staticItems.sources.some(s => s.id === staticItem.id && s.type === staticItem.type)) {
            regItems.sources.push(staticItem);
          } else if (staticItems.modules.some(m => m.id === staticItem.id && m.type === staticItem.type)) {
            regItems.modules.push(staticItem);
          } else if (staticItems.layout.some(l => l.id === staticItem.id && l.type === staticItem.type)) {
            regItems.layout.push(staticItem);
          } else if (staticItems.pipeline.some(p => p.id === staticItem.id && p.type === staticItem.type)) {
            regItems.pipeline.push(staticItem);
          }
        }
      }
    });

    return { favoriteItems: favItems, regularItems: regItems };
  }, [searchTerm, integrations, favorites, allPossibleItems]);


  // --- Temporarily disable mouse position tracking ---
  // useEffect(() => {
  //   const handleMouseMove = (e: globalThis.MouseEvent) => {
  //     setMousePosition({ x: e.clientX, y: e.clientY });
  //   };
  //
  //   window.addEventListener('mousemove', handleMouseMove);
  //   return () => {
  //     window.removeEventListener('mousemove', handleMouseMove);
  //   };
  // }, []);
  // --- End temporary disable ---

  // Get all visible items in a flat array for keyboard navigation (Favorites first)
  // TODO: This needs refinement to handle collapsed sections correctly for keyboard nav
  const allVisibleItems = useMemo<MenuItem[]>(() => {
    const mapItem = (item: StaticItemGroup | Integration): MenuItem => {
      const isIntegration = 'logo_url' in item; // Type guard
      return {
        nodeType: isIntegration ? "destination" : (item as StaticItemGroup).id as NodeType, // Use guard
        name: item.name,
        type: isIntegration ? item.name : (item as StaticItemGroup).type, // Use guard
        icon: isIntegration ? undefined : (item as StaticItemGroup).icon, // Use guard
        integration: isIntegration ? (item as Integration) : undefined, // Use guard
      };
    };

    // TODO: This mapping needs to respect collapsed sections for accurate keyboard nav index
    return [
      ...favoriteItems.map(mapItem),
      ...regularItems.sources.map(mapItem),
      ...regularItems.modules.map(mapItem),
      ...regularItems.layout.map(mapItem),
      ...regularItems.pipeline.map(mapItem),
      ...regularItems.destinations.map(mapItem),
    ];
  }, [favoriteItems, regularItems]);


  // Reset selection when search changes or when search input gets focus
  useEffect(() => {
    // Only highlight the first item if there's a search term
    if (searchTerm) {
      setSelectedIndex(0);
    } else {
      setSelectedIndex(-1);
    }
  }, [searchTerm]);
  
  // Set selectedIndex to 0 when search input gets focus
  useEffect(() => {
    const handleSearchFocus = () => {
      if (allVisibleItems.length > 0) {
        setSelectedIndex(0);
      }
    };
    
    const searchInput = searchInputRef.current;
    if (searchInput) {
      searchInput.addEventListener('focus', handleSearchFocus);
      return () => {
        searchInput.removeEventListener('focus', handleSearchFocus);
      };
    }
  }, [allVisibleItems.length, searchInputRef]);

  // Scroll selected item into view
  useEffect(() => {
    if (!menuContainerRef.current) return;

    const selectedElement = menuContainerRef.current.querySelector(
      `[data-index="${selectedIndex}"]`,
    );
    if (selectedElement) {
      selectedElement.scrollIntoView({
        block: "nearest",
        behavior: "smooth",
      });
    }
  }, [selectedIndex]);

  // Configure keyboard shortcuts
  const hotkeys = useMemo(
    () => [
      // This hotkey will be triggered when Enter is pressed while Canvas is in focus
      {
        key: "Enter",
        description: "Focus search field when Canvas is in focus",
        handler: () => {
          console.log("[LeftPanel] Enter hotkey handler triggered");
          console.log("[LeftPanel] searchInputRef:", searchInputRef.current);
          
          searchInputRef.current?.focus();
          console.log("[LeftPanel] Search input focused");
          
          // Scroll to the top of the panel to ensure the search field is visible
          if (menuContainerRef.current) {
            console.log("[LeftPanel] Scrolling menu container to top");
            menuContainerRef.current.scrollTop = 0;
          }
        },
        preventDefault: true,
      },
      {
        key: "Escape",
        description: "Clear search",
        handler: () => {
          if (searchTerm) {
            setSearchTerm("");
            searchInputRef.current?.blur();
          }
        },
      },
      {
        key: "ArrowUp",
        description: "Select previous item",
        handler: () => {
          if (document.activeElement === searchInputRef.current && allVisibleItems.length > 0) {
            setSelectedIndex((prev) =>
              prev > 0 ? prev - 1 : allVisibleItems.length - 1,
            );
          }
        },
      },
      {
        key: "ArrowDown",
        description: "Select next item",
        handler: () => {
          if (document.activeElement === searchInputRef.current && allVisibleItems.length > 0) {
            setSelectedIndex((prev) =>
              prev < allVisibleItems.length - 1 ? prev + 1 : 0,
            );
          }
        },
      },
      {
        key: "Enter",
        description: "Add selected item",
        handler: (event: KeyboardEvent) => { // Add event parameter with type
          console.log("[LeftPanel] Enter key for adding selected item");
          console.log("[LeftPanel] document.activeElement === searchInputRef.current:", document.activeElement === searchInputRef.current);
          console.log("[LeftPanel] allVisibleItems.length:", allVisibleItems.length);
          console.log("[LeftPanel] selectedIndex:", selectedIndex);
          
          if (document.activeElement === searchInputRef.current && allVisibleItems.length > 0 && selectedIndex >= 0) {
            console.log("[LeftPanel] Conditions met for adding selected item");
            const selectedItem = allVisibleItems[selectedIndex];
            console.log("[LeftPanel] Selected item:", selectedItem);
            
            const addHandlesValue = !event.shiftKey; // Capture the value
            console.log("[LeftPanel] Shift key pressed:", event.shiftKey);
            console.log("[LeftPanel] addHandles value passed to handleAdd:", addHandlesValue);

            // Set the keyboard selected node to position it under the mouse cursor
            setKeyboardSelectedNode(selectedItem);
            
            // Add the node to the canvas without specifying a custom position
            // This will use the smart positioning logic in handleAdd
            const newNode = handleAdd(
              selectedItem.nodeType,
              selectedItem.name,
              selectedItem.integration,
              undefined, // customPosition is undefined for keyboard add
              addHandlesValue // Pass addHandles based on shiftKey
            );
            
            // Select the newly added node and fit view to make it visible
            if (newNode) {
              // Select the node
              useStore.getState().setSelectedNode(newNode);
            }
            
            // Clear the keyboard selected node after a short delay
            setTimeout(() => {
              setKeyboardSelectedNode(null);
            }, 100);
            
            // Clear search and blur input
            setSearchTerm("");
            searchInputRef.current?.blur();
          }
        },
      },
    ],
    [searchTerm, allVisibleItems, selectedIndex, handleAdd],
  );

  useHotkeys(hotkeys);

  // Handle drag start
  const handleDragStart = (e: React.DragEvent, item: MenuItem) => {
    // Get the correct node type for the data transfer
    const nodeComponentType = getNodeComponentType(item.nodeType, item.name);
    
    // Generate consistent node data using the common function
    const nodeData = createNodeData(
      item.nodeType,
      item.name,
      item.type,
      item.integration,
      !e.shiftKey // Pass addHandles based on shiftKey
    );

    // Create a single object containing type and data, matching PlaybookLeftPanel
    const nodeInfo = {
      type: nodeComponentType, // Use the specific component type
      data: nodeData,
    };
    const nodeInfoString = JSON.stringify(nodeInfo);

    // Set the data transfer using only 'application/reactflow'
    e.dataTransfer.setData("application/reactflow", nodeInfoString);


    // Set drag effect
    e.dataTransfer.effectAllowed = "move";
    
    // Create a custom drag image element
    const dragPreview = document.createElement('div');
    dragPreview.className = 'drag-preview';
    dragPreview.style.position = 'absolute';
    dragPreview.style.top = '-1000px';
    dragPreview.style.left = '-1000px';
    dragPreview.style.width = '1px';
    dragPreview.style.height = '1px';
    dragPreview.style.opacity = '0.01';
    document.body.appendChild(dragPreview);
    
    // Use the invisible element as the drag image
    e.dataTransfer.setDragImage(dragPreview, 0, 0);
    
    // Clean up the element after a short delay
    setTimeout(() => {
      document.body.removeChild(dragPreview);
    }, 0);
    
    // Set dragging state
    setIsDragging(true);
    setDraggedItem(item);
    
    // Track mouse position
    setMousePosition({ x: e.clientX, y: e.clientY });
    
    // Add a class to the document body to change cursor during drag
    document.body.classList.add('dragging');
  };
  
  // Handle drag end
  const handleDragEnd = () => {
    setIsDragging(false);
    setDraggedItem(null);
    
    // Remove the dragging class from the body
    document.body.classList.remove('dragging');
  };

  // Update mouse position during drag
  const handleDrag = (e: React.DragEvent) => {
    if (e.clientX > 0 && e.clientY > 0) {
      setMousePosition({ x: e.clientX, y: e.clientY });
    }
  };
  
  // Track mouse movement globally to ensure smooth preview movement
  useEffect(() => {
    if (!isDragging) return;
    
    const handleMouseMove = (e: globalThis.MouseEvent) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, [isDragging]);

  // Animation variants for manual collapse/expand
  const variants = {
    expanded: { opacity: 1, x: 0, pointerEvents: 'auto' as const },
    manualCollapsed: { opacity: 1, x: -width, pointerEvents: 'auto' as const } // Slide left
  };

  // Calculate starting indices for each section (Needs adjustment for favorites & collapse)
  // TODO: Recalculate indices based on visible items in expanded sections
  let favoritesStartIndex = 0;
  let sourcesStartIndex = favoriteItems.length;
  let modulesStartIndex = sourcesStartIndex + regularItems.sources.length;
  let layoutStartIndex = modulesStartIndex + regularItems.modules.length;
  let pipelineStartIndex = layoutStartIndex + regularItems.layout.length;
  let destinationsStartIndex = pipelineStartIndex + regularItems.pipeline.length;


  return (
    <motion.div
      className={`fixed ${getPanelTopClass()} left-0 ${isPreviewMode ? 'h-screen' : 'h-[calc(100vh-56px)]'} bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex flex-col z-10`}
      initial="expanded"
      animate={isLeftPanelManuallyCollapsed ? "manualCollapsed" : "expanded"} // Use store state
      variants={variants}
      transition={{ type: "spring", stiffness: 300, damping: 30 }}
      style={{ width: `${width}px` }} // Keep width style for resize handle
    >
      {/* Tab Selector */}
      <TabSelector 
        activeTab={activeTab} 
        onTabChange={setActiveTab} 
        showIcons={width >= 280} 
      />
      
      {activeTab === 'library' ? (
        <>
          {/* Menu Container */}
          {/* Adjusted to flex-1 and overflow-y-auto to take available space */}
          <div ref={menuContainerRef} className="flex-1 overflow-y-auto"> 
        {/* Favorites Section */}
        <MenuSection
          title="FAVORITES"
          items={favoriteItems} // Pass the mixed array
          selectedIndex={selectedIndex}
          startIndex={favoritesStartIndex} // Starts at 0
          onAdd={handleAdd}
          onDragStart={handleDragStart}
          onDrag={handleDrag}
          onDragEnd={handleDragEnd}
          onToggleFavorite={handleToggleFavorite} // Pass handler
          favorites={favorites} // Pass favorites state
          onClearFavorites={handleClearFavorites} // Pass clear handler
        />

        {/* Sources Section */}
        <MenuSection
          title="SOURCES"
          items={regularItems.sources}
          selectedIndex={selectedIndex}
          startIndex={sourcesStartIndex} // Adjust index
          onAdd={handleAdd}
          onDragStart={handleDragStart}
          onDrag={handleDrag}
          onDragEnd={handleDragEnd}
          onToggleFavorite={handleToggleFavorite} // Pass handler
          favorites={favorites} // Pass favorites state
        />

        {/* Architecture Section */}
        <MenuSection
          title="ARCHITECTURE"
          items={regularItems.modules}
          selectedIndex={selectedIndex}
          startIndex={modulesStartIndex} // Adjust index
          onAdd={handleAdd}
          onDragStart={handleDragStart}
          onDrag={handleDrag}
          onDragEnd={handleDragEnd}
          onToggleFavorite={handleToggleFavorite} // Pass handler
          favorites={favorites} // Pass favorites state
        />

        {/* Layout Section */}
        <MenuSection
          title="LAYOUT"
          items={regularItems.layout}
          selectedIndex={selectedIndex}
          startIndex={layoutStartIndex} // Adjust index
          onAdd={handleAdd}
          onDragStart={handleDragStart}
          onDrag={handleDrag}
          onDragEnd={handleDragEnd}
          onToggleFavorite={handleToggleFavorite} // Pass handler
          favorites={favorites} // Pass favorites state
        />

        {/* Pipeline Section */}
        <MenuSection
          title="PIPELINE"
          items={regularItems.pipeline}
          selectedIndex={selectedIndex}
          startIndex={pipelineStartIndex} // Adjust index
          onAdd={handleAdd}
          onDragStart={handleDragStart}
          onDrag={handleDrag}
          onDragEnd={handleDragEnd}
          onToggleFavorite={handleToggleFavorite} // Pass handler
          favorites={favorites} // Pass favorites state
        />

        {/* Destinations Section */}
        <MenuSection
          title="DESTINATIONS"
          items={regularItems.destinations}
          isDestination={true} // Keep this for now, might refactor later
          selectedIndex={selectedIndex}
          startIndex={destinationsStartIndex} // Adjust index
          onAdd={handleAdd}
          onDragStart={handleDragStart}
          onDrag={handleDrag}
          onDragEnd={handleDragEnd}
          onToggleFavorite={handleToggleFavorite} // Pass handler
          favorites={favorites} // Pass favorites state
        />

        {/* Empty State (Adjusted logic) */}
        {favoriteItems.length === 0 &&
         Object.values(regularItems).every((items) => items.length === 0) && (
          <div className="px-3 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
            No items found
          </div>
        )}
          </div>
          {/* Search Bar - Moved to the bottom */}
          <div className="p-3 border-t border-gray-200 dark:border-gray-700"> {/* Added border-t for separation */}
            <SearchBar
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              searchInputRef={searchInputRef}
            />
          </div>
        </>
      ) : (
        /* AI Assistant */
        <div className="flex-1 overflow-hidden"> {/* Ensure AI Assistant also takes full height */}
          <AIAssistant isActive={activeTab === 'ai-assistant'} />
        </div>
      )}
      
      {/* Resize Handle */}
      <motion.div
        className="absolute right-0 top-0 bottom-0 w-2 group hover:bg-blue-100 dark:hover:bg-blue-900 transition-colors duration-150"
        style={{ cursor: 'default' }}
        onPanStart={(e, _) => {
          dragControls.start(e);
        }}
        onPan={(_, info) => {
          const newWidth = Math.max(240, Math.min(600, width + info.delta.x));
          setWidth(newWidth);
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.cursor = 'col-resize';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.cursor = 'default';
        }}
      >
        <div className="absolute right-0 top-0 bottom-0 w-[1px] bg-gray-200 dark:bg-gray-700 group-hover:bg-blue-400 dark:group-hover:bg-blue-500 transition-colors duration-150" />
      </motion.div>
      
      {/* Drag preview */}
      {isDragging && draggedItem && (
        <DragPreview item={draggedItem} position={mousePosition} />
      )}

      {/* Manual Collapse/Expand Button */}
      <motion.button
        className="absolute right-[-26px] top-[8%] z-20 p-1 bg-white dark:bg-gray-700 border-t border-r border-b border-gray-300 dark:border-gray-900 rounded-r-md shadow-md hover:bg-gray-100 dark:hover:bg-gray-600" // Positioned on the right
        onClick={togglePanels} // Use togglePanels from store
        title={isLeftPanelManuallyCollapsed ? "Expand Panel" : "Collapse Panel"} // Use store state
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }} // Fade in slightly after panel appears
      >
        {isLeftPanelManuallyCollapsed ? (
          <ChevronRight size={16} className="text-gray-600 dark:text-gray-300" /> // Icon direction reversed
        ) : (
          <ChevronLeft size={16} className="text-gray-600 dark:text-gray-300" /> // Icon direction reversed
        )}
      </motion.button>
    </motion.div>
  );
}

export default LeftPanel;
